import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import CustomHelmet from 'components/CustomHelmet';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, useMediaQuery, withStyles } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Routes from 'Routes';
import { FETCH_ASSETS_URL } from 'appenv';
import { selectLocale } from 'models/localization';
import { selectLocalizedEventName } from 'models/virtual_event';
import remoteTimeNow from 'libs/remote-time-now';
import ClockIcon from 'components/icons/Clock';
import isVideoRTCSupported from 'utils/isVideoRTCSupported';
import TableList from './components/TableList';
import UnsupportedBrowserWarning from './components/UnsupportedBrowserWarning';

const BackgroundUrl = `${FETCH_ASSETS_URL}/assets/networking_lounge_background.jpg`;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  background: url(${BackgroundUrl});
  position: relative;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 1440px auto;

  @media (min-width: 1440px), (min-height: 960px) {
    background-size: cover;
  }
`;

const RootContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  overflow: auto;
`;

const DesktopDialogContent = withStyles({
  root: {
    width: '490px',
    paddingBottom: '24px',
    textAlign: 'center',
  },
})(DialogContent);

const MobileDialogContent = withStyles({
  root: {
    paddingBottom: '24px',
    textAlign: 'center',
  },
})(DialogContent);

const ModalTitle = styled.h4`
  text-align: center;
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
`;

const BackToLobbyButton = withStyles({
  root: {
    backgroundColor: '#5C8DF3',
    color: '#FFF',
    width: '100%',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#5C8DF3',
      color: '#FFF',
    },
  },
})(Button);

const NetworkingLounge = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const [duringBlockingPeriods, setDuringBlockingPeriods] = useState(false);
  const { pageNames: { networkingLounge }, networkingLoungeConfig = {} } = useSelector((state: any) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);
  const pageTitle = networkingLounge?.[locale] || t('page.networking_lounge', 'Networking Lounge');
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const history = useHistory();
  const DialogContentContainer = useMemo(() => (isDesktop ? DesktopDialogContent : MobileDialogContent), [isDesktop]);
  const isAvailable = isVideoRTCSupported();

  const onBackToLobbyBtnClick = () => {
    history.push(Routes.lobby);
  };

  useEffect(() => {
    if (!isAvailable) return;
    const now = remoteTimeNow();
    setDuringBlockingPeriods(now.isAfter(moment(networkingLoungeConfig?.blockingPeriodStartTime)) && now.isBefore(moment(networkingLoungeConfig?.blockingPeriodEndTime)));
  }, []);

  return (
    <>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`${pageTitle} | ${eventName}`}</title>
        <meta name="description" content={`${pageTitle} page of ${eventName}.`} />
      </CustomHelmet>
      <PageContainer>
        <RootContainer>
          {
            isAvailable ? (
              !duringBlockingPeriods && <TableList />
            ) : (
              <UnsupportedBrowserWarning />
            )
          }
          <Dialog
            open={duringBlockingPeriods}
            disableBackdropClick
            aria-describedby="alert-dialog-description"
          >
            <DialogContentContainer>
              <ClockIcon color="#EAB53E" width="96px" height="96px" />
              <ModalTitle>
                {t('networking_lounge.blocking_period_modal.title', 'Please come back later!')}
              </ModalTitle>
              <DialogContentText>
                {t('networking_lounge.blocking_period_modal.description', 'The Networking Lounge is temporarily closed by the organizer.')}
              </DialogContentText>
              <BackToLobbyButton onClick={onBackToLobbyBtnClick}>
                {t('networking_lounge.blocking_period_modal.back_to_lobby', 'Back to lobby')}
              </BackToLobbyButton>
            </DialogContentContainer>
          </Dialog>
        </RootContainer>
      </PageContainer>
    </>
  );
};

export default NetworkingLounge;
